/**
 * BroadcastDialog — Settings > Mailler > "Duyuru Gönder".
 *
 * Super admin, tenant'taki TÜM aktif kullanıcılara tek seferlik duyuru
 * e-postası yollar. Hazır "son güncellemeler" taslağı otomatik yüklenir;
 * göndermeden önce tek adrese test atılabilir.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Send, Users as UsersIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function BroadcastDialog({ open, onOpenChange }) {
  const { user } = useAuthStore();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [testTo, setTestTo] = useState("");
  const [activeCount, setActiveCount] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTestTo((prev) => prev || user?.email || "");
    setLoadingPrefill(true);
    Promise.all([
      api.get("/emails/broadcast/prefill").catch(() => ({ data: null })),
      api.get("/users").catch(() => ({ data: [] })),
    ])
      .then(([pre, usersRes]) => {
        const d = pre.data;
        if (d) {
          setSubject((prev) => prev || d.subject || "");
          setBody((prev) => prev || d.body || "");
          setCtaUrl((prev) => prev || d.cta_url || "");
          setCtaLabel((prev) => prev || d.cta_label || "");
        }
        const users = usersRes.data || [];
        setActiveCount(users.filter((u) => u.active !== false && u.email).length);
      })
      .finally(() => setLoadingPrefill(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const payload = () => ({
    subject: subject.trim(),
    body,
    cta_url: ctaUrl.trim() || null,
    cta_label: ctaLabel.trim() || null,
  });

  const valid = subject.trim() && body.trim();

  const sendTest = async () => {
    if (!valid || !testTo.trim()) return;
    setSendingTest(true);
    try {
      const { data } = await api.post("/emails/broadcast", {
        ...payload(),
        test_to: testTo.trim(),
      });
      if (data?.delivery?.delivered) {
        toast.success(`Test duyurusu ${testTo.trim()} adresine gönderildi`);
      } else {
        toast.warning("Gönderim outbox'a düştü — SendGrid yapılandırmasını kontrol edin");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test gönderilemedi");
    } finally {
      setSendingTest(false);
    }
  };

  const sendAll = async () => {
    setConfirmOpen(false);
    setSendingAll(true);
    try {
      const { data } = await api.post("/emails/broadcast", payload());
      toast.success(
        `Duyuru gönderildi — ${data.sent}/${data.total} başarılı${data.failed ? `, ${data.failed} başarısız` : ""}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Duyuru gönderilemedi");
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-violet-600" /> Duyuru Gönder
            </DialogTitle>
            <DialogDescription>
              Şirketteki tüm aktif kullanıcılara e-posta duyurusu yollayın.
              Hazır &quot;son güncellemeler&quot; taslağı otomatik yüklendi — dilediğiniz
              gibi düzenleyebilirsiniz. Gövde HTML destekler.
            </DialogDescription>
          </DialogHeader>

          {loadingPrefill ? (
            <div className="flex items-center gap-2 py-8 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Taslak yükleniyor…
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label htmlFor="broadcast-subject">Konu</Label>
                <Input
                  id="broadcast-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="broadcast-subject-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="broadcast-body">İçerik (HTML destekli)</Label>
                <Textarea
                  id="broadcast-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  data-testid="broadcast-body-input"
                  className="mt-1 font-mono text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="broadcast-cta-label">Buton yazısı</Label>
                  <Input
                    id="broadcast-cta-label"
                    value={ctaLabel}
                    onChange={(e) => setCtaLabel(e.target.value)}
                    data-testid="broadcast-cta-label-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="broadcast-cta-url">Buton linki</Label>
                  <Input
                    id="broadcast-cta-url"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    data-testid="broadcast-cta-url-input"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <Label htmlFor="broadcast-test-to" className="text-xs text-zinc-600">
                  Önce test gönder (yalnızca bu adrese)
                </Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    id="broadcast-test-to"
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="ornek@firma.com"
                    data-testid="broadcast-test-to-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={sendTest}
                    disabled={!valid || !testTo.trim() || sendingTest}
                    data-testid="broadcast-test-btn"
                    className="shrink-0 gap-1.5"
                  >
                    {sendingTest ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Test gönder
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="items-center gap-2 sm:justify-between">
            <span
              className="inline-flex items-center gap-1.5 text-xs text-zinc-500"
              data-testid="broadcast-recipient-count"
            >
              <UsersIcon className="h-3.5 w-3.5" />
              {activeCount === null ? "…" : `${activeCount} aktif kullanıcıya gidecek`}
            </span>
            <Button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!valid || sendingAll || loadingPrefill}
              data-testid="broadcast-send-all-btn"
              className="gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
            >
              {sendingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Megaphone className="h-4 w-4" />
              )}
              Tüm kullanıcılara gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="broadcast-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Duyuru herkese gönderilsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{subject}&quot; konulu duyuru{" "}
              {activeCount === null ? "tüm" : activeCount} aktif kullanıcıya
              e-posta olarak gönderilecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="broadcast-confirm-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={sendAll}
              data-testid="broadcast-confirm-btn"
              className="bg-violet-600 hover:bg-violet-700"
            >
              Evet, gönder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
